/**
 * House standards for media delivery QA, mirroring the media-api entities.
 *
 * Hand-written rather than taken from the generated SDK because the guidelines
 * endpoints are not in it yet. Keep in step with
 * `apps/media-api/src/entity/media-guideline.entity.ts` and
 * `apps/media-api/src/media-guidelines/dto`.
 */
import { i18n } from '@/shared/i18n';

export type GuidelineKind =
  | 'required_value'
  | 'numeric_bound'
  | 'naming_pattern'
  | 'reference_list'
  | 'conditional'
  | 'cross_level'
  | 'tracking_url'
  | 'attestation'
  | 'advisory';

/** Kinds the backend will currently accept. The rest are declared but refused. */
export const IMPLEMENTED_GUIDELINE_KINDS: GuidelineKind[] = [
  'required_value',
  'numeric_bound',
  'naming_pattern',
];

/** One position in a naming convention, as the editor writes it. */
export interface NamingSegmentInput {
  label: string;
  oneOf?: string[];
}

// Getters in every label map below: a resolved value here would freeze English at import.
export const GUIDELINE_KIND_LABELS: Record<GuidelineKind, string> = {
  get required_value() {
    return i18n.t('knowledge:mediaGuideline.types.kind.requiredValue');
  },
  get numeric_bound() {
    return i18n.t('knowledge:mediaGuideline.types.kind.numericBound');
  },
  get naming_pattern() {
    return i18n.t('knowledge:mediaGuideline.types.kind.namingPattern');
  },
  get reference_list() {
    return i18n.t('knowledge:mediaGuideline.types.kind.referenceList');
  },
  get conditional() {
    return i18n.t('knowledge:mediaGuideline.types.kind.conditional');
  },
  get cross_level() {
    return i18n.t('knowledge:mediaGuideline.types.kind.crossLevel');
  },
  get tracking_url() {
    return i18n.t('knowledge:mediaGuideline.types.kind.trackingUrl');
  },
  get attestation() {
    return i18n.t('knowledge:mediaGuideline.types.kind.attestation');
  },
  get advisory() {
    return i18n.t('knowledge:mediaGuideline.types.kind.advisory');
  },
};

/** Governance tiers, shared with the taxonomy work. */
export type GuidelineTier = 'block' | 'warn' | 'advise';

export const GUIDELINE_TIER_LABELS: Record<GuidelineTier, string> = {
  get block() {
    return i18n.t('knowledge:mediaGuideline.types.tier.block');
  },
  get warn() {
    return i18n.t('knowledge:mediaGuideline.types.tier.warn');
  },
  get advise() {
    return i18n.t('knowledge:mediaGuideline.types.tier.advise');
  },
};

/** Whether a guideline is enforced at all, separate from whether a version is published. */
export type GuidelineStatus = 'active' | 'superseded' | 'unenforceable';

export const GUIDELINE_STATUS_LABELS: Record<GuidelineStatus, string> = {
  get active() {
    return i18n.t('knowledge:mediaGuideline.types.status.active');
  },
  get superseded() {
    return i18n.t('knowledge:mediaGuideline.types.status.superseded');
  },
  get unenforceable() {
    return i18n.t('knowledge:mediaGuideline.types.status.unenforceable');
  },
};

export type GuidelineVersionStatus = 'draft' | 'published' | 'archived';

export type GuidelineLevel = 'campaign' | 'adGroup' | 'ad' | 'creative';

export const GUIDELINE_LEVEL_LABELS: Record<GuidelineLevel, string> = {
  get campaign() {
    return i18n.t('knowledge:mediaGuideline.types.level.campaign');
  },
  get adGroup() {
    return i18n.t('knowledge:mediaGuideline.types.level.adGroup');
  },
  get ad() {
    return i18n.t('knowledge:mediaGuideline.types.level.ad');
  },
  get creative() {
    return i18n.t('knowledge:mediaGuideline.types.level.creative');
  },
};

export interface MediaGuidelineVersion {
  id: string;
  versionNumber: number;
  parentVersionId: string | null;
  status: GuidelineVersionStatus;
  level: GuidelineLevel;
  tier: GuidelineTier;
  rule: Record<string, unknown>;
  why: string | null;
  howToFix: string | null;
  referenceUrl: string | null;
  ownerName: string | null;
  revision: number;
  publishedAt: string | null;
  createdAt: string;
}

export interface MediaGuideline {
  id: string;
  name: string;
  description: string | null;
  kind: GuidelineKind;
  /** Null applies to every platform. */
  platform: string | null;
  /** Null applies to every campaign category. */
  campaignCategory: string | null;
  status: GuidelineStatus;
  currentVersionId: string | null;
  lastVerifiedAt: string | null;
  supersededById: string | null;
  libraryKey: string | null;
  createdAt: string;
  updatedAt: string;
  /** Present on detail reads only, newest first. */
  versions?: MediaGuidelineVersion[];
}

export interface ListMediaGuidelinesResponse {
  items: MediaGuideline[];
  total: number;
  page: number;
  pageSize: number;
}

export interface QaAuditEntry {
  id: string;
  actorId: string;
  action: string;
  reason: string | null;
  guidelineVersionId: string | null;
  jobId: string | null;
  nodeId: string | null;
  metadata: Record<string, unknown>;
  createdAt: string;
}

/** The rule body for `required_value`, the one kind with an evaluator today. */
export interface RequiredValueRule {
  fieldKey: string;
  expected: string | number | boolean;
}

export interface CreateMediaGuidelineInput {
  name: string;
  description?: string;
  kind: GuidelineKind;
  platform?: string;
  campaignCategory?: string;
  level: GuidelineLevel;
  tier: GuidelineTier;
  rule: Record<string, unknown>;
  why?: string;
  howToFix?: string;
  referenceUrl?: string;
  ownerName?: string;
}

export interface UpdateGuidelineDraftInput {
  expectedRevision: number;
  name?: string;
  description?: string;
  level: GuidelineLevel;
  tier: GuidelineTier;
  rule: Record<string, unknown>;
  why?: string;
  howToFix?: string;
  referenceUrl?: string;
  ownerName?: string;
}

/** The version of a guideline a reader should see: the published one, else the draft. */
export function effectiveVersion(guideline: MediaGuideline): MediaGuidelineVersion | undefined {
  const versions = guideline.versions ?? [];
  return (
    versions.find((version) => version.id === guideline.currentVersionId) ??
    versions.find((version) => version.status === 'draft') ??
    versions[0]
  );
}

/**
 * The version an author is working on: the open draft, else the published one.
 *
 * The mirror image of `effectiveVersion`, and the distinction is load-bearing. Starting a
 * draft leaves `currentVersionId` on the published version, so a reader's answer to "which
 * version is in force" is the wrong answer to "which version am I editing". Asking the
 * reader's question on an authoring screen made a published guideline uneditable: the
 * publish gate saw `published` and refused with "there is no open draft", the editor seeded
 * itself from the enforced rule rather than the draft, and saving wrote those stale values
 * back over the draft.
 */
export function editableVersion(guideline: MediaGuideline): MediaGuidelineVersion | undefined {
  const versions = guideline.versions ?? [];
  return (
    versions.find((version) => version.status === 'draft') ??
    versions.find((version) => version.id === guideline.currentVersionId) ??
    versions[0]
  );
}

/**
 * Whether an unpublished draft exists, asked directly rather than inferred from whichever
 * version a screen happens to be showing. A published guideline with a draft open answers
 * true here while `effectiveVersion` still reports the published one.
 */
export function hasOpenDraft(guideline: MediaGuideline): boolean {
  return (guideline.versions ?? []).some((version) => version.status === 'draft');
}

/** A guideline with no published version is not enforcing anything yet. */
export function isEnforced(guideline: MediaGuideline): boolean {
  return guideline.status === 'active' && guideline.currentVersionId !== null;
}
